// Copy to branch… (section 6.1, the legacy cherry-pick of documents): a dry run of one documents:batch on the target
// branch shows created / updated / unchanged / failed per document before Apply, which runs the batch for real.
import { useEffect, useMemo, useState } from 'react';
import { ApiError } from '../../api/client';
import { ops } from '../../api/operations';
import type { BatchOperationRequest, BatchResponse, Document, Json } from '../../api/types';
import { useGame } from '../../shell/GameContext';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Chip';
import { Field } from '../../ui/Field';
import { AlertCircle, AlertTriangle, GitBranch } from '../../ui/icons';
import { Checkbox, Select } from '../../ui/inputs';
import { EmptyState } from '../../ui/Banner';
import { Modal } from '../../ui/Modal';
import { useToasts } from '../../ui/Toast';
import { entityKey } from '../../workspace/Workspace';
import { useWidgetEnv } from '../widgets/context';

export function CopyToBranchDialog({ docs, onClose }: { docs: Document[]; onClose: () => void }) {
  const { catalog, client, org, game, branch, workspace } = useWidgetEnv();
  const { branches } = useGame();
  const toasts = useToasts();
  const targets = branches.filter((b) => b.name !== branch);
  const [target, setTarget] = useState(targets[0]?.name ?? '');
  const [withReferences, setWithReferences] = useState(false);
  const [preview, setPreview] = useState<BatchResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const dirty = docs.filter((d) => workspace.getDraft(entityKey('document', d.id)));

  const closure = useMemo(() => {
    const out = new Map<string, Document>();
    const visit = (d: Document, depth: number) => {
      if (out.has(d.id)) return;
      out.set(d.id, d);
      if (!withReferences || depth > 6) return;
      const t = catalog.templates.get(d.template);
      const data = (d.data ?? {}) as Record<string, Json>;
      for (const p of t?.effectiveParameters ?? []) {
        const ids = p.type === 'document' && typeof data[p.name] === 'string' ? [data[p.name] as string] : p.type === 'list' && p.of?.type === 'document' && Array.isArray(data[p.name]) ? (data[p.name] as Json[]).filter((x): x is string => typeof x === 'string') : [];
        for (const id of ids) {
          const ref = catalog.resolve(id) as Document | undefined;
          if (ref) visit(ref, depth + 1);
        }
      }
    };
    for (const d of docs) visit(d, 0);
    return Array.from(out.values());
  }, [docs, withReferences, catalog]);

  const operations = async (): Promise<BatchOperationRequest[]> => {
    const ids = closure.map((d) => d.id);
    const existing = new Map<string, Document>();
    for (let i = 0; i < ids.length; i += 50) {
      const page = await client.data(ops.listDocuments, { path: { org, game, branch: target }, query: { filter: JSON.stringify([{ field: 'id', op: 'in', value: ids.slice(i, i + 50) }]), limit: 200 } });
      for (const d of page.items) existing.set(d.id, d);
    }
    return closure.map((d) => {
      const there = existing.get(d.id);
      const base = { tempId: null, ifVersion: null, unnyId: d.unnyId ?? null, meta: d.meta ?? null } as BatchOperationRequest;
      if (there) return { ...base, op: 'update', id: d.id, template: null, ifVersion: there.version, data: d.data, export: d.export, tags: d.tags };
      return { ...base, op: 'create', id: d.id, template: d.template, data: d.data, export: d.export, tags: d.tags };
    });
  };
  const run = async (dryRun: boolean) => {
    if (!target) return;
    setBusy(true);
    setError(null);
    try {
      const body = { atomic: false, dryRun, message: `Copy ${closure.length} document${closure.length === 1 ? '' : 's'} from ${branch}`, operations: await operations() };
      const result = await client.data(ops.batchDocuments, { path: { org, game, branch: target }, body });
      setPreview(result);
      if (!dryRun) {
        const applied = result.results.filter((r) => r.status === 'applied').length;
        toasts.success(`${applied} document${applied === 1 ? '' : 's'} copied to ${target}${result.commitId ? ` (commit ${result.commitId.slice(0, 8)})` : ''}.`);
        onClose();
      }
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message}${e.details?.length ? ` — ${e.details.map((d) => d.message).join('; ')}` : ''}` : String(e));
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    setPreview(null);
  }, [target, withReferences]);

  return (
    <Modal
      title={`Copy ${docs.length === 1 ? catalog.label(docs[0]!) : `${docs.length} documents`} to a branch`}
      open
      onClose={onClose}
      size="m"
      footer={
        <>
          <span className="spacer" />
          <Button onClick={onClose}>Cancel</Button>
          <Button onClick={() => void run(true)} disabled={!target || busy || dirty.length > 0} busy={busy && !preview}>
            Dry run
          </Button>
          <Button variant="primary" onClick={() => void run(false)} disabled={!target || busy || dirty.length > 0 || !preview} busy={busy && !!preview}>
            Apply
          </Button>
        </>
      }
    >
      <div className="stack dialog-form">
        {targets.length === 0 && <EmptyState icon={<GitBranch />} title="No other branch" body="This game has no other branch to copy to." />}
        {dirty.length > 0 && (
          <div className="banner banner-warning" role="alert">
            <AlertTriangle className="banner-icon" aria-hidden="true" />
            <div className="banner-body">{dirty.length} of the documents have unsaved changes: save them first (the copy reads what the branch has).</div>
          </div>
        )}
        <Field label="Target branch">
          <Select value={target} onChange={(e) => setTarget(e.target.value)} aria-label="Target branch">
            {targets.map((b) => (
              <option key={b.id} value={b.name}>
                {b.name}
                {b.isProtected ? ' (protected)' : ''}
              </option>
            ))}
          </Select>
        </Field>
        <Checkbox label={`Include the documents these reference (${closure.length} in all)`} checked={withReferences} onChange={(e) => setWithReferences(e.target.checked)} />
        {error && (
          <div className="banner banner-danger" role="alert">
            <AlertCircle className="banner-icon" aria-hidden="true" />
            <div className="banner-body">{error}</div>
          </div>
        )}
        {preview && (
          <ul className="small ref-list change-list">
            {preview.results.map((r) => {
              const d = closure[Number(r.index)];
              const tone = r.status === 'applied' || r.status === 'skipped' ? 'success' : 'danger';
              return (
                <li key={r.index} className="row">
                  <Badge tone={tone}>{r.status === 'skipped' ? 'ok' : r.status}</Badge>
                  <span>{d ? catalog.label(d) : String(r.index)}</span>
                  {r.error && <span className="field-error">{r.error.message}</span>}
                </li>
              );
            })}
          </ul>
        )}
        <p className="muted small">The copy is one commit on the target branch; this branch's workspace is not involved.</p>
      </div>
    </Modal>
  );
}
